import { useState, useEffect, useCallback, useMemo } from 'react'
import { PostType } from 'src/hook/usePosts'
import {
  togglePostSidebarFullScreenEventDispatch,
  togglePostModalEventDispatch,
  togglePostLeaveModalEventDispatch
} from 'src/utils/customEvents'
import { MODAL_ACTIONS } from 'src/utils/customEvents/types'

import { FileProps } from 'src/components/dropZoneUploader'
import { useSchedulePostForm } from 'src/hook/useSchedulePostForm'
import { TipTapMention } from '../Posts/tiptap'
import { PostModalDefaultProps } from './postModalPublication.types'
import {
  usePostModal,
  PostModal,
  PostLeaveModal,
  PostSidebarFull,
  Media,
  usePostModalFullScreen,
  usePostLeaveModal
} from '.'

const PostModalPublication = () => {
  const { isOpen, suggestionIA, isFullScreen, handleSuggestionIA } = usePostModal()
  const { isOpenPostFullScreenSidebar, listenerPostSidebarFull } = usePostModalFullScreen()
  const { isOpenLeaveModal, setIsOpenLeaveModal, listenerLeaveModal } = usePostLeaveModal()
  const { resetForm: resetScheduleForm } = useSchedulePostForm()

  // FORM
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [descriptionOnlyText, setDescriptionOnlyText] = useState<string>('')
  const [postType, setPostType] = useState<PostType>(PostType.TEXT)
  const [media, setMedia] = useState<Media>({} as Media)
  const [currentMedia, setCurrentMedia] = useState<string>('')
  const [fileUploaded, setFileUploaded] = useState<FileProps>({} as FileProps)
  const [uploading, setUploading] = useState<boolean>(false)
  const [isOpenUpload, setIsOpenUpload] = useState<boolean>(false)
  const [linkUrl, setLinkUrl] = useState<string>('')
  const [mentions, setMentions] = useState<TipTapMention[]>([])

  useEffect(() => {
    listenerPostSidebarFull()
  }, [listenerPostSidebarFull])

  useEffect(() => {
    listenerLeaveModal()
  }, [listenerLeaveModal])

  useEffect(() => {
    if (suggestionIA.title && suggestionIA.description) {
      setTitle(suggestionIA.title)
      setDescription(suggestionIA.description)
    }
  }, [suggestionIA])

  useEffect(() => {
    if (linkUrl) {
      setPostType(PostType.URL)
    }
  }, [linkUrl])

  const handleResetForm = () => {
    setTitle('')
    setDescription('')
    setPostType(PostType.TEXT)
    setMedia({} as Media)
    setCurrentMedia('')
    setFileUploaded({} as FileProps)
    setUploading(false)
    setIsOpenUpload(false)
    handleSuggestionIA({ title: '', description: '' })
    resetScheduleForm()
    setLinkUrl('')
  }

  const handleOnClose = useCallback(() => {
    togglePostModalEventDispatch({ action: MODAL_ACTIONS.CLOSE })
    ;(title || description || currentMedia || linkUrl) &&
      togglePostLeaveModalEventDispatch({ action: MODAL_ACTIONS.OPEN })
  }, [
    title,
    description,
    currentMedia,
    linkUrl,
    togglePostModalEventDispatch,
    togglePostLeaveModalEventDispatch
  ])

  const handleLeaveModalClose = useCallback(() => {
    handleResetForm()
    togglePostLeaveModalEventDispatch({ action: MODAL_ACTIONS.CLOSE })
  }, [handleResetForm, togglePostLeaveModalEventDispatch])

  const handlePostSidebarFullClose = useCallback(() => {
    togglePostSidebarFullScreenEventDispatch({ action: MODAL_ACTIONS.CLOSE })
    ;(title || description || currentMedia || linkUrl) &&
      togglePostLeaveModalEventDispatch({ action: MODAL_ACTIONS.OPEN })
  }, [
    title,
    description,
    currentMedia,
    linkUrl,
    togglePostSidebarFullScreenEventDispatch,
    togglePostLeaveModalEventDispatch
  ])

  const handleKeepEditing = useCallback(() => {
    setIsOpenLeaveModal(false)

    if (!isFullScreen) {
      return togglePostModalEventDispatch({ action: MODAL_ACTIONS.OPEN })
    }

    return togglePostSidebarFullScreenEventDispatch({ action: MODAL_ACTIONS.OPEN })
  }, [
    isFullScreen,
    setIsOpenLeaveModal,
    togglePostModalEventDispatch,
    togglePostSidebarFullScreenEventDispatch
  ])

  const onRemoveMedia = () => {
    setCurrentMedia('')
    setFileUploaded({} as FileProps)
    setMedia({} as Media)
    setPostType(PostType.TEXT)
  }

  const isFileUploaded = useMemo(
    () => Boolean(fileUploaded && fileUploaded.name && fileUploaded.size && currentMedia),
    [fileUploaded, currentMedia]
  )

  const defaultProps: PostModalDefaultProps = {
    title,
    description,
    postType,
    media,
    currentMedia,
    uploading,
    isOpenUpload,
    fileUploaded,
    isFileUploaded,
    descriptionOnlyText,
    linkUrl,
    mentions,
    setLinkUrl,
    setDescriptionOnlyText,
    setFileUploaded,
    setTitle,
    setDescription,
    setPostType,
    setMedia,
    setCurrentMedia,
    setUploading,
    setIsOpenUpload,
    onRemoveMedia,
    handleResetForm,
    setMentions
  }

  return (
    <>
      <PostModal isOpen={isOpen} onClose={handleOnClose} {...defaultProps} />

      <PostSidebarFull
        isOpen={isOpenPostFullScreenSidebar}
        onClose={handlePostSidebarFullClose}
        {...defaultProps}
      />

      <PostLeaveModal
        isOpen={isOpenLeaveModal}
        onClose={handleLeaveModalClose}
        onKeepEditing={handleKeepEditing}
      />
    </>
  )
}

export default PostModalPublication
